import { QuizMode, QuizResponse, QuizPlayer, RoundState } from "./QuizProtocol";

export interface QuizState {
  mode: QuizMode;
  question: string;
  roundState: RoundState;
  responses: QuizResponse[];
  showResults: boolean;
  hasSubmitted: boolean;
  players: QuizPlayer[];
  hostId: number;
  totalPlayers: number;
  responded: number;
  oCount?: number;
  xCount?: number;
  results?: {
    mode: QuizMode;
    question: string;
    responses: QuizResponse[];
    oCount?: number;
    xCount?: number;
  };
}

export const initialState: QuizState = {
  mode: QuizMode.BUZZER,
  question: "",
  roundState: RoundState.WAITING,
  responses: [],
  showResults: false,
  hasSubmitted: false,
  players: [],
  hostId: 0,
  totalPlayers: 0,
  responded: 0,
};

export type QuizAction =
  | { type: "SET_MODE"; payload: QuizMode }
  | { type: "UPDATE_GAME_STATE"; payload: {
      mode: QuizMode;
      players: QuizPlayer[];
      roundState: RoundState;
      question: string;
      responses: QuizResponse[];
    }}
  | { type: "ROUND_STARTED"; payload: { question: string; mode: QuizMode }}
  | { type: "ROUND_STOPPED" }
  | { type: "PLAYER_RESPONDED"; payload: {
      playerId: number;
      playerName: string;
      order?: number;
      mode: QuizMode;
    }}
  | { type: "RESULTS_REVEALED"; payload: {
      mode: QuizMode;
      question: string;
      totalPlayers: number;
      responded: number;
      responses: QuizResponse[];
      oCount?: number;
      xCount?: number;
    }}
  | { type: "SET_HAS_SUBMITTED"; payload: boolean }
  | { type: "RESET_LOCAL_STATE" }
  | { type: "ROUND_CONTINUED"; payload: { question: string; mode: QuizMode } };

const quizReducer = (state: QuizState, action: QuizAction): QuizState => {
  switch (action.type) {
    case "SET_MODE":
      return {
        ...state,
        mode: action.payload,
        question: "",
        roundState: RoundState.WAITING,
        responses: [],
        hasSubmitted: false,
        showResults: false,
      };

    case "UPDATE_GAME_STATE":
      return {
        ...state,
        mode: action.payload.mode,
        players: action.payload.players,
        roundState: action.payload.roundState,
        question: action.payload.question,
        responses: action.payload.responses,
        hostId: action.payload.players.find(p => p.isHost)?.id || 0,
        totalPlayers: action.payload.players.length,
      };

    case "ROUND_STARTED":
      return {
        ...state,
        question: action.payload.question,
        mode: action.payload.mode,
        roundState: RoundState.ACTIVE,
        responses: [],
        hasSubmitted: false,
        showResults: false,
        responded: 0,
        oCount: 0,
        xCount: 0,
      };

    case "ROUND_STOPPED":
      return {
        ...state,
        roundState: RoundState.STOPPED,
      };

    case "PLAYER_RESPONDED":
      // Add real-time response tracking
      const newResponse: QuizResponse = {
        playerId: action.payload.playerId,
        playerName: action.payload.playerName,
        response: action.payload.mode === QuizMode.BUZZER ? Date.now() : "",
        order: action.payload.order,
        timestamp: Date.now(),
      };

      // Update existing response or add new one
      const existingIndex = state.responses.findIndex(
        r => r.playerId === action.payload.playerId
      );
      
      let updatedResponses = [...state.responses];
      if (existingIndex === -1) {
        updatedResponses.push(newResponse);
      } else {
        updatedResponses[existingIndex] = newResponse;
      }

      return {
        ...state,
        responses: updatedResponses,
        responded: updatedResponses.length,
      };

    case "RESULTS_REVEALED":
      return {
        ...state,
        showResults: true,
        responses: action.payload.responses,
        totalPlayers: action.payload.totalPlayers,
        responded: action.payload.responded,
        oCount: action.payload.oCount,
        xCount: action.payload.xCount,
        results: {
          mode: action.payload.mode,
          question: action.payload.question,
          responses: action.payload.responses,
          oCount: action.payload.oCount,
          xCount: action.payload.xCount,
        },
      };

    case "SET_HAS_SUBMITTED":
      return {
        ...state,
        hasSubmitted: action.payload,
      };

    case "RESET_LOCAL_STATE":
      return {
        ...state,
        question: "",
        roundState: RoundState.WAITING,
        responses: [],
        showResults: false,
        hasSubmitted: false,
        responded: 0,
        oCount: 0,
        xCount: 0,
        results: undefined,
      };

    case "ROUND_CONTINUED":
      return {
        ...state,
        question: action.payload.question,
        mode: action.payload.mode,
        roundState: RoundState.ACTIVE,
        showResults: false,
        // responses는 유지 (초기화하지 않음)
        // hasSubmitted도 유지 (이미 제출한 플레이어는 제출 상태 유지)
      };

    default:
      return state;
  }
};

export default quizReducer;